import "./style.scss";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  OutlinedInput,
  IconButton,
  InputAdornment,
  Alert,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Link, Navigate, redirect, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { LockIcon, QatarIcon } from "../../assets/icons";
import { checkIf, checkEmptyObj, getUserRole, UserRoles, checkEmpty, siteKey } from "../../utils";
import useLogin from "../Auth/useLogin";
import { RootState } from "../../store";
import { set } from "../../store/globalSlice";
import { useMediaQuery } from "@react-hook/media-query";
import { useTranslation } from "react-i18next";
import Api from "../../openapi";
import moment from "moment";
import ReCAPTCHA from "react-google-recaptcha";
import useRecaptcha from "../../hooks/useRecaptcha";
import UserNotFoundDialog from "../Dialog/userNotFound";

interface IError {
  show?: boolean;
  message?: string;
}

export interface IFieldValidate {
  status?: boolean;
  message?: string;
  dirty?: boolean;
}
export interface ILoginValidate {
  show?: boolean;
  password?: IFieldValidate;
  phone?: IFieldValidate;
  age?: IFieldValidate;
  birthDate?: IFieldValidate;
  height?: IFieldValidate;
  weight?: IFieldValidate;
  targetWeight?: IFieldValidate;
  gender?: IFieldValidate;
  fullName?: IFieldValidate;
  verification?: IFieldValidate;
  otp?: IFieldValidate;
}

export default function Login() {
  const { t, i18n } = useTranslation("login");
  const isRTL = i18n.language === "ar";
  let [searchParams, setSearchParams] = useSearchParams();
  const invitationCode = searchParams.get("code");
  const user = useSelector((state: RootState) => state.user);
  const mobile = useMediaQuery("only screen and (max-width: 600px)");
  const navigate = useNavigate();
  const [phoneNumber, setPhone] = useState("");
  const [areaCode, setAreaCode] = useState("+974");
  const [otp, setOtp] = useState("");
  const [innerStep, setInnerStep] = useState(0); // 0 = phone entry, 1 = OTP entry
  const [loading, setLoading] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(180);
  const [resendDisabled, setResendDisabled] = useState(true);
  const dispatch = useDispatch();
  const { loginWithOtp } = useLogin();
  const [errorObj, setError] = useState<IError>();
  const { capchaToken, recaptchaRef, handleRecaptcha } = useRecaptcha();
  const [validation, setValidation] = useState<ILoginValidate>({
    show: false,
    phone: {
      dirty: false,
      status: true,
      message: t("phoneEmptyError"),
    },
    otp: {
      dirty: false,
      status: true,
      message: t("otpEmptyError"),
    },
  });
  const [showUserNotFoundDialog, setShowUserNotFoundDialog] = useState(false);



  const resetForm = () => {
    setPhone("");
    setOtp("");
    setInnerStep(0);
  };

  const handleUserNotFoundDialogClose = (value: boolean) => {
    setShowUserNotFoundDialog(value);
    // Reset reCAPTCHA when dialog closes so user can try again
    if (!value) {
      handleRecaptcha("");
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  const formatTimer = (seconds: number) => {
    return moment(moment.duration(seconds, "seconds").asMilliseconds()).locale(i18n.language).format("mm:ss");
  };

  const createInterval = () => {
    let timeoutSeconds = 180;
    let interval = setInterval(() => {
      if (timeoutSeconds !== 0) {
        setTimerSeconds(--timeoutSeconds);
      } else if (timeoutSeconds === 0) {
        clearInterval(interval);
        setResendDisabled(false);
      }
    }, 1000);
    return interval;
  };
  const handlePhone = (event: any) => {
    const value = event.target.value;
    const regex = /^[3567]{1}\d{7}$/;
    const test = regex.test(value);
    setPhone(value);
    let status = !test;
    setValidation((state) => ({
      ...state,
      phone: {
        status,
        message: t("phoneFormatError"),
        dirty: true,
      },
    }));
  };

  const handleOtp = (event: any) => {
    const value = event.target.value;
    const regex = /^\d{6}$/;
    const test = regex.test(value);
    setOtp(value);
    let status = !test;
    setValidation((state) => ({
      ...state,
      otp: {
        status,
        message: t("otpFormatError"),
        dirty: true,
      },
    }));
  };

  const handleSendOtp = async (event: any) => {
    event.preventDefault();
    setError({ show: false, message: "" });
    setValidation((state) => ({
      ...state,
      show: true,
      phone: {
        ...state.phone,
        dirty: true,
      },
    }));
    if (validation.phone!.status) {
      return;
    }
    if (!capchaToken) {
      return;
    }

    setLoading(true);
    const fullPhoneNumber = `${areaCode}${phoneNumber}`;

    try {
      await Api.Otp.sendOtp({ phoneNumber: fullPhoneNumber, captchaToken: capchaToken, checkMembership: true });
      setInnerStep(1);
      createInterval();
    } catch (error: any) {
      setShowUserNotFoundDialog(true);
    }
    setLoading(false);
  };

  const handleLogin = async (event: any) => {
    event.preventDefault();
    setError({ show: false, message: "" });
    setValidation((state) => ({
      ...state,
      show: true,
      otp: {
        ...state.otp,
        dirty: true,
      },
    }));
    if (validation.otp!.status) {
      return;
    }

    setLoading(true);
    const fullPhoneNumber = `${areaCode}${phoneNumber}`;

    try {
      await loginWithOtp({ phoneNumber: fullPhoneNumber, otpCode: otp });
      resetForm();
      dispatch(set({ showLoginModal: false }));
    } catch (error: any) {
      const errorData = error?.response?.data;
      setError({
        show: true,
        message: errorData?.message || t("invalidOtpError"),
      });
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    setError({ show: false, message: "" });
    setOtp("");
    setResendDisabled(true);
    setTimerSeconds(180);
    handleRecaptcha("");
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }

    const fullPhoneNumber = `${areaCode}${phoneNumber}`;

    try {
      await Api.Otp.sendOtp({ phoneNumber: fullPhoneNumber, captchaToken: capchaToken });
      createInterval();
    } catch (error: any) {
      const errorData = error?.response?.data;
      setError({
        show: true,
        message: errorData?.message || t("resendOtpError"),
      });
    }
  };

  return (
    <Box
      sx={{ padding: ["0 16px", "32px"], direction: isRTL ? "rtl" : "ltr" }}
      className="login_wrapper"
      component="form"
      noValidate
    >
      <div className="title">{t("title")}</div>
      {/* <div className="subtitle">Please enter your phone number and password to login.</div> */}
      <Typography mb={2} component={"span"} sx={{ fontSize: "14px", fontWeight: 600, color: "text.fontDefault" }}>
        {t("phoneNumber")}
      </Typography>
      <div className={`phone_container ${isRTL ? "rtl" : ""}`}>
        <div className="area_code">
          <QatarIcon />
          <TextField className="text_input" value={areaCode} />
        </div>
        <TextField
          id="phone"
          name="phone"
          className="text_input phone_input"
          value={phoneNumber}
          disabled={innerStep === 1}
          error={validation.show && validation.phone?.dirty && validation.phone.status}
          helperText={validation.show && validation.phone?.status ? validation.phone.message : undefined}
          onChange={handlePhone}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
        />
      </div>

      {innerStep === 0 && (
        <>
          {/* <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
            <Link to="/forgot-password" style={{ textDecoration: "none" }}>
              <Typography sx={{ fontSize: "14px", color: "text.fontDefault", fontWeight: 500 }}>
                {t("forgotPassword")}
              </Typography>
            </Link>
          </Box> */}
          <Box sx={{ display: "flex", marginTop: "0px", marginBottom: "16px" }}>
            <ReCAPTCHA ref={recaptchaRef} sitekey={String(siteKey)} size="normal" onChange={handleRecaptcha} />
          </Box>
        </>
      )}

      {innerStep === 1 && (
        <>
          <Typography mb={2} component={"span"} sx={{ fontSize: "14px", fontWeight: 600, color: "text.fontDefault" }}>
            {t("otpCode")}
          </Typography>
          <div className="password_container">
            <TextField
              id="otp"
              name="otp"
              className="text_input"
              value={otp}
              onChange={handleOtp}
              error={validation.show && validation.otp?.dirty && validation.otp.status}
              helperText={validation.show && validation.otp?.status ? validation.otp.message : undefined}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
              InputProps={{
                ...(isRTL
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <LockIcon width={22} height={22} />
                        </InputAdornment>
                      ),
                    }
                  : {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon width={22} height={22} />
                        </InputAdornment>
                      ),
                    }),
              }}
            />
          </div>
        </>
      )}
      <div className="login-btn">
        {errorObj?.show && (
          <Alert severity="error" sx={{ marginBottom: "12px" }}>
            {errorObj?.message}
          </Alert>
        )}
        {innerStep === 0 ? (
          <LoadingButton
            type="submit"
            color="primary"
            loading={loading}
            variant="contained"
            disableElevation
            onClick={handleSendOtp}
            disabled={!capchaToken}
          >
            {loading ? <span>&nbsp;</span> : <span>{t("sendVerificationCode")}</span>}
          </LoadingButton>
        ) : (
          <>
            <LoadingButton
              type="submit"
              color="primary"
              loading={loading}
              variant="contained"
              disableElevation
              onClick={handleLogin}
              disabled={checkEmpty(otp) || otp.length !== 6}
            >
              {loading ? <span>&nbsp;</span> : <span>{t("login")}</span>}
            </LoadingButton>
            {timerSeconds === 0 ? (
              <>
                <Box sx={{ display: "flex", marginTop: "12px" }}>
                  <ReCAPTCHA ref={recaptchaRef} sitekey={String(siteKey)} size="normal" onChange={handleRecaptcha} />
                </Box>
                <LoadingButton
                  fullWidth
                  sx={{ marginTop: "12px" }}
                  onClick={handleResendOtp}
                  variant="outlined"
                  disableRipple
                  disableTouchRipple
                  disabled={resendDisabled || !capchaToken}
                >
                  {t("resendCode")}
                </LoadingButton>
              </>
            ) : (
              <Box mt={2} sx={{ display: "flex", justifyContent: "center" }}>
                <Typography sx={{ fontSize: "14px", color: "text.fontDefault" }}>
                  {t("resendCodeIn", { timer: formatTimer(timerSeconds) })}
                </Typography>
              </Box>
            )}
          </>
        )}
      </div>
      <div className="exp">{t("dontHaveAccount")}</div>
      <Box className="join-btn">
        <Link to={`${checkIf(invitationCode) ? "/quick-register?code=" + invitationCode : "/join"}`}>
          <Button color="primary" variant="outlined" disableElevation>
            {t("joinNow")}
          </Button>
        </Link>
      </Box>
      <UserNotFoundDialog open={showUserNotFoundDialog} close={handleUserNotFoundDialogClose} />
    </Box>
  );
}
